import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserInfo } from '@app/core/models';
import { TwofactorCredentials } from '@app/core/models/twfactor-credendial-model';
import { TwofactorModel } from '@app/core/models/two-factor-model';
import { TranslationService } from '@app/core/services';
import { BrowserStorageService } from '@app/core/services/browser-storage.Service';
import { CheckVerifyService } from '@app/core/services/check-verify.service';
import { NotificationService } from '@app/core/services/notification.service';
import { akitaStorage } from '@app/core/services/state/akita-storage';
import { UserInfoService } from '@app/core/services/state/user-info';
import { BehaviorSubject, Observable, Subscription, catchError, finalize, map, of, take } from 'rxjs';
import { APP_CONFIG, IAppConfig } from '../../config/app.config';
import { AuthTokenType } from '../models/auth-token-type.enum';
import { Credentials } from '../models/credentials';
import { User } from '../models/user';
import { UserFeaturesService } from '@app/core/services/state/user-features';
import { jwtDecode } from "jwt-decode";
import { TokenRefreshService } from './token-refresh.service';
import { KycVerificationService } from '@app/shared/services/kyc-verification.service';
import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from '@evva/capacitor-secure-storage-plugin';

declare var window: any
@Injectable({
	providedIn: 'root'
})
export class AuthService {

	private authStatusSource = new BehaviorSubject<boolean>(false);
	private rememberMeToken = 'rememberMe_token';
	authStatus$ = this.authStatusSource.asObservable();
	private twoFactorAuth = new BehaviorSubject<TwofactorModel>(null);
	twoFactorAuth$ = this.twoFactorAuth.asObservable();
	roles: string[];
	private refreshTokenSubscription: Subscription;
	private isNative = Capacitor.isNativePlatform();

	private nativeTokenCache: { [key: string]: string } = {};

	// Promise عمومی که نشون می‌ده cache آمادست یا نه
	// هر کسی که نیاز به isLoggedIn() مطمئن دارد (مثل Guard) باید منتظرش بماند
	public nativeCacheReadyPromise: Promise<void>;

	constructor(
		private http: HttpClient,
		private router: Router,
		private userInfoService: UserInfoService,
		private userFeaturesService: UserFeaturesService,
		private translationService: TranslationService,
		private notificationService: NotificationService,
		private browserStorageService: BrowserStorageService,
		private checkVerifyService: CheckVerifyService,
		@Inject(APP_CONFIG) private appConfig: IAppConfig,
		private tokenRefreshService: TokenRefreshService,
		private kycVerificationService: KycVerificationService
	) {
		if (this.isNative) {
			this.nativeCacheReadyPromise = this.hydrateNativeCache();
		} else {
			this.nativeCacheReadyPromise = Promise.resolve();
			this.updateStatusOnPageRefresh();
		}
	}

	private async hydrateNativeCache(): Promise<void> {
		try {
			const accessToken = await SecureStoragePlugin.get({ key: AuthTokenType[AuthTokenType.AccessToken] }).catch((e) => {
				console.error('hydrateNativeCache: get accessToken failed', e);
				return null;
			});
			const refreshToken = await SecureStoragePlugin.get({ key: AuthTokenType[AuthTokenType.RefreshToken] }).catch((e) => {
				console.error('hydrateNativeCache: get refreshToken failed', e);
				return null;
			});
			this.nativeTokenCache[AuthTokenType[AuthTokenType.AccessToken]] = accessToken?.value || null;
			this.nativeTokenCache[AuthTokenType[AuthTokenType.RefreshToken]] = refreshToken?.value || null;
		} finally {
			this.updateStatusOnPageRefresh();
		}
	}

	async login(credentials: Credentials): Promise<boolean> {
		this.deleteAuthTokens();
		return this.http
			.post<User>(this.appConfig.apiEndpoint + '/v2/account/login', credentials)
			.toPromise()
			.then(async (response: any) => {
				if (response.data['accessToken'] !== null && response.data['accessToken'].length > 2) {
					this.sendGtag(response.data['accessToken'])
					return await this.setInfoAfterLogin(response, true);
				} else {
					this.updateTwoFactor({
						client_id: response.data.client_id,
						email: credentials.email
					});
					this.router.navigate(['/auth/authnticator-code']);
				}
			});
	}

	async newLogin(response: any, email: string) {
		if (response.data['accessToken'] !== null && response.data['accessToken'].length > 2) {
			this.sendGtag(response.data['accessToken'])
			return await this.setInfoAfterLogin(response, true);
		} else {
			this.updateTwoFactor({
				client_id: response.data.client_id,
				email: email
			});
			return 'twoFactor';
		}
	}
	async newTwoFactor(response: any) {
		const userInfo = await this.setInfoAfterLogin(response, true);
		return userInfo;
	}



	sendGtag(token: string) {
		const decoded = jwtDecode(token);
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			'event': 'login',
			'userId': decoded?.sub
		})
	}

	async twofactorLogin(credentials: TwofactorCredentials): Promise<boolean> {
		this.deleteAuthTokens();
		return this.http
			.post<User>(this.appConfig.apiEndpoint + '/v2/account/login/2fa', credentials)
			.toPromise()
			.then(async (response: any) => {
				const userInfo = await this.setInfoAfterLogin(response, true);
				return userInfo;
			});
	}

	async setInfoAfterLogin(response: any, rememberMe: boolean): Promise<boolean> {
		this.roles = response.data.roles.toLowerCase().split(',');
		this.userFeaturesService.update({ features: response.data.features.toLowerCase() });
		this.browserStorageService.setLocal(
			this.rememberMeToken,
			rememberMe
		);

		if (!response) {
			this.authStatusSource.next(false);
			return false;
		}

		// مهم: حالا منتظر می‌مانیم تا توکن واقعاً ذخیره شود قبل از ادامه
		await this.setLoginSession(response);
		this.authStatusSource.next(true);
		this.notificationService.scheduleNotification();

		return await this.getUserInformation();
	}

	updateTwoFactor(value: TwofactorModel): void {
		this.twoFactorAuth.next(value);
	}

	dec(s) {
		var base64Url = s.split('.')[1];
		var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
		var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
			return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
		}).join(''));

		return JSON.parse(jsonPayload);
	}

	getUserInformation(): Promise<boolean> {
		return new Promise((resolve, reject) => {
			const url = `${this.appConfig.apiEndpoint}/v2/profile/info`;
			this.http.get(url)
				.toPromise()
				.then(
					(response: any) => {
						const userInfo: UserInfo = response['data'] || {};
						userInfo.roles = this.roles;
						this.userInfoService.updateInfo(userInfo);
						// if (!Capacitor.isNativePlatform()) {
						// 	this.checkVersion(userInfo.configurations.version);
						// }
						resolve(true);
					});
		});
	}

	// checkVersion(version: string): void {
	// 	const currentVersion = localStorage.getItem('version');
	// 	if (!currentVersion) {
	// 		localStorage.setItem('version', version);
	// 		return;
	// 	}
	// 	if (currentVersion === version) {
	// 		return;
	// 	}

	// 	localStorage.setItem('version', version);
	// 	window.location.reload();
	// }

	logout(keepReturnUrl?: boolean): void {
		this.authStatusSource.next(false);
		this.notificationService.deleteRefreshNotificationTimer();
		const url = "/auth";
		if (keepReturnUrl) {
			this.router.navigate([url], { queryParams: { returnUrl: window.location.pathname } });
		} else {
			this.router.navigate([url]);
		}
		this.logoutOnServer();
		this.deleteAuthTokens();
	}

	logoutOnServer(): void {
		const url = `${this.appConfig.apiEndpoint}/idp/v3/accounts/logout`;
		this.http.get(url).pipe(take(1)).subscribe();
	}

	// =========================================================
	// متد اصلی — همچنان sync (برای backward compatibility)
	// ⚠️ روی native اگه قبل از resolve شدن nativeCacheReadyPromise صدا زده شود،
	// ممکنه نتیجه نادرست بدهد. برای Guard ها از isLoggedInSafe() استفاده کنید.
	// =========================================================
	isLoggedIn(): boolean {
		const accessToken = this.getRawAuthToken(AuthTokenType.AccessToken);
		const refreshToken = this.getRawAuthToken(AuthTokenType.RefreshToken);
		if (this.isEmptyString(accessToken) || this.isEmptyString(refreshToken)) {
			return false;
		}

		let isExpired = false;
		this.isTokenExpired(accessToken).subscribe(value => isExpired = value);

		return !isExpired;
	}

	// =========================================================
	// نسخه امن برای استفاده در Guard ها — منتظر می‌ماند تا cache آماده شود
	// =========================================================
	async isLoggedInSafe(): Promise<boolean> {
		if (this.isNative) {
			await this.nativeCacheReadyPromise;
		}
		return this.isLoggedIn();
	}

	private isEmptyString(value: string): boolean {
		return !value || 0 === value.length;
	}

	private isTokenExpired(token: string): Observable<boolean> {
		try {
			const payload = JSON.parse(atob(token.split('.')[1]));
			const expiry = payload.exp;
			if (Math.floor(Date.now() / 1000) < expiry) {
				return of(false);
			}

			return this.tokenRefreshService.handleRefreshToken().pipe(
				map(response => {
					this.setLoginSession(response);
					return false;
				}),
				catchError(() => of(true))
			);
		} catch (e) {
			return of(true);
		}
	}

	rememberMe(): boolean {
		return (
			this.browserStorageService.getLocal(this.rememberMeToken) === true
		);
	}

	getRawAuthToken(tokenType: AuthTokenType): string {
		const key = AuthTokenType[tokenType];
		if (this.isNative) {
			return this.nativeTokenCache[key] || null;
		} else {
			if (this.rememberMe()) {
				return this.browserStorageService.getLocal(key);
			} else {
				return this.browserStorageService.getSession(key);
			}
		}
	}

	deleteAuthTokens(): void {
		const currentLang = this.translationService.getCurrentLang();
		const hideEvent = localStorage.getItem('hideAppLunched');
		const restoreTours = this.preserveLocalByPrefix(['tour:']);
		akitaStorage.clear();

		if (this.isNative) {
			this.nativeTokenCache[AuthTokenType[AuthTokenType.AccessToken]] = null;
			this.nativeTokenCache[AuthTokenType[AuthTokenType.RefreshToken]] = null;
			SecureStoragePlugin.remove({ key: AuthTokenType[AuthTokenType.AccessToken] }).catch(() => { });
			SecureStoragePlugin.remove({ key: AuthTokenType[AuthTokenType.RefreshToken] }).catch(() => { });
		} else {
			if (this.rememberMe()) {
				this.browserStorageService.removeLocal(AuthTokenType[AuthTokenType.AccessToken]);
				this.browserStorageService.removeLocal(AuthTokenType[AuthTokenType.RefreshToken]);
			} else {
				this.browserStorageService.removeSession(AuthTokenType[AuthTokenType.AccessToken]);
				this.browserStorageService.removeSession(AuthTokenType[AuthTokenType.RefreshToken]);
			}
		}

		this.browserStorageService.removeLocal('RefreshTokenStatus');
		this.browserStorageService.removeLocal(this.rememberMeToken);
		localStorage.setItem('language', currentLang.uniqueSeoCode);
		restoreTours();
		if (hideEvent) {
			localStorage.setItem('hideAppLunched', 'true');
		}
	}

	// مهم: حالا async شد و واقعاً منتظر ذخیره می‌ماند (قبلاً fire-and-forget بود)
	async setLoginSession(response: any): Promise<void> {
		this.browserStorageService.setLocal('RefreshTokenStatus', false);
		await this.setToken(AuthTokenType.AccessToken, response.data.accessToken);
		await this.setToken(AuthTokenType.RefreshToken, response.data.refreshToken);
	}

	private async setToken(tokenType: AuthTokenType, tokenValue: string): Promise<void> {
		const key = AuthTokenType[tokenType];
		if (this.isNative) {
			this.nativeTokenCache[key] = tokenValue;
			try {
				// مهم: حالا منتظر می‌مانیم تا واقعاً ذخیره شود
				await SecureStoragePlugin.set({ key, value: tokenValue });
			} catch (e) {
				console.error('SecureStorage set failed', e);
			}
		} else {
			if (this.rememberMe()) {
				this.browserStorageService.setLocal(key, tokenValue);
			} else {
				this.browserStorageService.setSession(key, tokenValue);
			}
		}
	}

	isAuthUserInRole(requiredPermission: string): boolean {
		const permissions = this.browserStorageService.getUserInformation();
		let hasPermission = false;
		for (const i of permissions) {
			if (permissions[i] === requiredPermission) {
				hasPermission = true;
				break;
			}
		}
		return hasPermission;
	}

	private updateStatusOnPageRefresh(): void {
		if (this.isLoggedIn()) {
			this.notificationService.scheduleNotification();
		}
	}

	private preserveLocalByPrefix(prefixes: string[]) {
		const saved: Record<string, string> = {};
		for (let i = 0; i < localStorage.length; i++) {
			const k = localStorage.key(i)!;
			if (prefixes.some(p => k.startsWith(p))) {
				const v = localStorage.getItem(k);
				if (v != null) saved[k] = v;
			}
		}
		return () => {
			Object.entries(saved).forEach(([k, v]) => localStorage.setItem(k, v));
		};
	}
}